import { useTranslation } from 'react-i18next'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

import { DetailsCard } from '../../../../components/molecules/DetailsCard/DetailsCard'
import { formatValueWithUnit } from '../../../../utils/formatValueWithUnit'
import { getUnitSymbol } from '../../../../hooks/useApi/utils/processUsageCostResponse'
import { AmountWithUnit, InvoiceDetailsResponse } from '../../../../hooks'
import CommodityBadgeList from './CommodityBadgeList'

type InvoiceDetailsProps = {
    invoice: Partial<InvoiceDetailsResponse>
    loading?: boolean
}

export const InvoiceDetails: React.FC<InvoiceDetailsProps> = (props) => {
    const { t } = useTranslation('translations')
    const { invoice, loading } = props
    const invoiceDetailsItems = useInvoiceDetailsItems(invoice, !!loading)
    const invoicePaymentDetailsItems = useInvoicePaymentDetailsItems(
        invoice,
        !!loading
    )

    return (
        <div className="grid gap-6 md:grid-cols-2">
            <DetailsCard
                title={t('invoice.details.invoice.title')}
                items={invoiceDetailsItems}
            />
            <DetailsCard
                title={t('invoice.details.payment.title')}
                items={invoicePaymentDetailsItems}
            />
        </div>
    )
}

const useInvoiceDetailsItems = (
    invoice: InvoiceDetailsProps['invoice'],
    loading: boolean
) => {
    const { t } = useTranslation('translations')

    const items = [
        {
            label: t('invoice.details.invoice.invoiceNo'),
            value: invoice.invoiceNo,
        },
        {
            label: t('invoice.details.invoice.accountNo'),
            value: invoice.accountNo,
        },
        {
            label: t('invoice.details.invoice.commodity'),
            value: invoice.commodities ? (
                <CommodityBadgeList commodities={invoice.commodities} />
            ) : undefined,
        },
        {
            label: t('invoice.details.invoice.provider'),
            value: invoice.providers ? (
                <InvoiceProviders providers={invoice.providers} />
            ) : undefined,
        },
        {
            label: t('invoice.details.invoice.invoiceDate'),
            value: invoice.invoiceDate
                ? formatDate(invoice.invoiceDate)
                : undefined,
        },
        {
            label: t('invoice.details.invoice.submitDate'),
            value: invoice.submitDate
                ? formatDate(invoice.submitDate)
                : undefined,
        },
    ]

    return items.map((item) => ({ ...item, loading }))
}

const useInvoicePaymentDetailsItems = (
    invoice: InvoiceDetailsProps['invoice'],
    loading: boolean
) => {
    const { t } = useTranslation('translations')

    const items = [
        {
            label: t('invoice.details.payment.dueDate'),
            value: invoice.dueDate ? formatDate(invoice.dueDate) : undefined,
        },
        {
            label: t('invoice.details.payment.invoiceTotal'),
            value: invoice.invoiceTotal
                ? formatAmount(invoice.invoiceTotal)
                : undefined,
        },
        {
            label: t('invoice.details.payment.currentCharges'),
            value: invoice.currentCharges
                ? formatAmount(invoice.currentCharges)
                : undefined,
        },
        {
            label: t('invoice.details.payment.balanceForward'),
            value: invoice.balanceForward
                ? formatAmount(invoice.balanceForward)
                : undefined,
        },
        {
            label: t('invoice.details.payment.lateFee'),
            value: invoice.lateFee ? formatAmount(invoice.lateFee) : undefined,
        },
    ]

    return items.map((item) => ({ ...item, loading }))
}

const formatAmount = (amount?: AmountWithUnit) => {
    if (!amount || !amount.value || !amount.unit) {
        return null
    }

    return formatValueWithUnit({
        value: amount.value,
        unit: getUnitSymbol(amount.unit),
    })
}

const formatDate = (timepstamp?: number) => {
    return dayjs.utc(timepstamp).format('MM/DD/YYYY')
}

const InvoiceProviders: React.FC<{
    providers: Required<InvoiceDetailsResponse>['providers']
}> = ({ providers }) => {
    return providers.map((provider) => provider.providerName).join('; ')
}
